import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Sparkles, ArrowRight, CheckCircle2, AlertCircle } from "lucide-react";
import { SITE } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Team() {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    looking_for: "",
  });
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", msg: "" });
    try {
      await axios.post(`${API}/apply`, form);
      setStatus({ state: "success", msg: "Application received. We'll be in touch shortly." });
      setForm({ first_name: "", last_name: "", email: "", phone: "", looking_for: "" });
    } catch (err) {
      setStatus({
        state: "error",
        msg: err?.response?.data?.detail?.[0]?.msg || "Something went wrong. Please try again.",
      });
    }
  };

  return (
    <section
      id="team"
      data-testid="team-section"
      className="relative bg-[#08080A] py-24 lg:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        <div className="lg:col-span-6">
          <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-[#F5A623] font-semibold">
            <span className="divider-gold" /> Join Our Team
          </span>
          <h2
            data-testid="team-headline"
            className="mt-5 font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.08] tracking-tight silver-text"
          >
            Become an independent <em className="text-[#F5A623]">wealth consultant</em>.
          </h2>
          <p className="mt-5 text-[#B7BBC4] leading-relaxed">{SITE.team.intro}</p>

          <div className="mt-10 grid sm:grid-cols-2 gap-8">
            <div>
              <p className="text-xs uppercase tracking-[0.24em] text-white font-semibold">
                What you can expect from us
              </p>
              <ul className="mt-4 space-y-2.5">
                {SITE.team.expect.map((x) => (
                  <li key={x} className="flex items-center gap-2.5 text-sm text-[#B7BBC4]">
                    <Sparkles className="w-3.5 h-3.5 text-[#F5A623]" />
                    {x}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.24em] text-white font-semibold">
                Benefits you'll gain
              </p>
              <ul className="mt-4 space-y-2.5">
                {SITE.team.benefits.map((x) => (
                  <li key={x} className="flex items-center gap-2.5 text-sm text-[#B7BBC4]">
                    <Sparkles className="w-3.5 h-3.5 text-[#F5A623]" />
                    {x}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-6 glass-card rounded-md p-7 lg:p-9"
          data-testid="apply-form"
        >
          <h3 className="font-serif text-2xl text-white">Apply now</h3>
          <p className="text-sm text-[#9CA0AA] mt-1">Let us know why you'd be a great fit.</p>

          <div className="mt-6 grid sm:grid-cols-2 gap-4">
            <Field label="First name" required value={form.first_name} onChange={update("first_name")} testId="apply-first-name" />
            <Field label="Last name" required value={form.last_name} onChange={update("last_name")} testId="apply-last-name" />
            <Field label="Email" type="email" required value={form.email} onChange={update("email")} testId="apply-email" />
            <Field label="Phone" required value={form.phone} onChange={update("phone")} testId="apply-phone" />
          </div>
          <div className="mt-4">
            <Label>I'm looking to...</Label>
            <textarea
              data-testid="apply-looking-for"
              value={form.looking_for}
              onChange={update("looking_for")}
              rows={4}
              placeholder="Tell us about yourself and your goals."
              className="mt-1.5 w-full rounded-md input-dark px-3.5 py-2.5 text-sm resize-none"
            />
          </div>

          <button
            type="submit"
            disabled={status.state === "loading"}
            data-testid="apply-submit"
            className="mt-7 group inline-flex items-center gap-3 px-6 py-3 rounded-full bg-[#F5A623] text-[#0A0A0C] text-sm font-medium tracking-wide hover:bg-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-60"
          >
            {status.state === "loading" ? "Submitting..." : "Submit application"}
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </button>

          {status.state === "success" && (
            <p className="mt-4 inline-flex items-center gap-2 text-sm text-emerald-400" data-testid="apply-success">
              <CheckCircle2 className="w-4 h-4" /> {status.msg}
            </p>
          )}
          {status.state === "error" && (
            <p className="mt-4 inline-flex items-center gap-2 text-sm text-red-400" data-testid="apply-error">
              <AlertCircle className="w-4 h-4" /> {status.msg}
            </p>
          )}
        </motion.form>
      </div>
    </section>
  );
}

function Label({ children }) {
  return (
    <label className="text-xs uppercase tracking-[0.18em] text-white font-semibold">
      {children}
    </label>
  );
}

function Field({ label, type = "text", required, value, onChange, testId }) {
  return (
    <div>
      <Label>{label}{required && " *"}</Label>
      <input
        data-testid={testId}
        type={type}
        required={required}
        value={value}
        onChange={onChange}
        className="mt-1.5 w-full rounded-md input-dark px-3.5 py-2.5 text-sm"
      />
    </div>
  );
}
